import React, { useState, useEffect ,useRef} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { setSnackInfo } from '../../redux/actions/appAction';
import HttpService from '../../services/httpService';

/* ─── Global styles ────────────────────────────────────────────────────── */
const PAGE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;0,800;1,600&family=DM+Sans:wght@300;400;500;600&display=swap');

.foid-page { animation: foidFadeIn .35s ease both; }
@keyframes foidFadeIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
@keyframes spin { to { transform: rotate(360deg); } }

.foid-spec-card { transition: box-shadow .18s, transform .18s; }
.foid-spec-card:hover { box-shadow: 0 6px 24px rgba(27,42,74,.1) !important; }

.foid-img-wrap img { transition: transform .5s cubic-bezier(.25,.46,.45,.94); }
.foid-img-wrap:hover img { transform: scale(1.05); }

.foid-back-btn { transition: background .15s, transform .15s; }
.foid-back-btn:hover { background: rgba(255,255,255,.2) !important; }
.foid-back-btn:active { transform: scale(.96); }

.foid-tab-btn { transition: all .18s; border-bottom: 2.5px solid transparent; }
.foid-tab-btn.active { border-bottom-color: #1B2A4A !important; color: #1B2A4A !important; font-weight: 700; }
.foid-tab-btn:hover:not(.active) { color: #374151 !important; background: #F8F9FD; }

.foid-badge-pulse { animation: badgePulse 2.4s ease-in-out infinite; }
@keyframes badgePulse { 0%,100%{ box-shadow: 0 0 0 0 rgba(5,150,105,.35); } 60%{ box-shadow: 0 0 0 7px rgba(5,150,105,0); } }

.foid-price-shine {
  background: linear-gradient(90deg, #1B2A4A 0%, #374B6E 40%, #1B2A4A 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: priceShine 3s linear infinite;
}
@keyframes priceShine { to { background-position: 200% center; } }

.foid-divider-dot { display:inline-block; width:4px; height:4px; border-radius:50%; background:#D1D5DB; margin:0 10px; vertical-align:middle; }

.foid-expand-content { overflow: hidden; transition: max-height 0.3s ease, opacity 0.25s ease; }

.foid-edit-specs-btn { transition: background .15s, box-shadow .15s, transform .12s; }
.foid-edit-specs-btn:hover { background: #253660 !important; box-shadow: 0 4px 16px rgba(27,42,74,.25) !important; }
.foid-edit-specs-btn:active { transform: scale(.97); }

.foid-modal-textarea:focus { border-color: #1B2A4A !important; outline: none; box-shadow: 0 0 0 3px rgba(27,42,74,.08); }
`;

let cssInjected = false;
function injectPageCSS() {
    if (cssInjected || typeof document === 'undefined') return;
    const s = document.createElement('style');
    s.textContent = PAGE_CSS;
    document.head.appendChild(s);
    cssInjected = true;
}

/* ─── Helpers ──────────────────────────────────────────────────────────── */
const fmt = (n) => '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });

/* ─── Spec config ──────────────────────────────────────────────────────── */
const SPECS = [
    { key: 'item_desc',           label: 'Description',    icon: '📖', accent: '#1B2A4A', bg: '#F8F9FD', border: '#EAEFF8' },
    { key: 'item_material',       label: 'Material',        icon: '🪵', accent: '#78350F', bg: '#FEF3C7', border: '#FDE68A' },
    { key: 'item_finish',         label: 'Finish',          icon: '✨', accent: '#1E40AF', bg: '#EFF6FF', border: '#BFDBFE' },
    // { key: 'item_color',          label: 'Colour',          icon: '🎨', accent: '#5B21B6', bg: '#F5F3FF', border: '#DDD6FE' },
    { key: 'item_dimensions',     label: 'Dimensions',      icon: '📐', accent: '#065F46', bg: '#ECFDF5', border: '#A7F3D0' },
    { key: 'item_whats_included', label: "What's Included", icon: '📦', accent: '#92400E', bg: '#FFF7ED', border: '#FED7AA' },
];

/* ─── Frontend key → backend field name ───────────────────────────────── */
const FIELD_MAP = {
    item_desc:           'item_description',
    item_material:       'item_material_specification',
    item_finish:         'item_finish_details',
    // item_color:          'item_color_specification',
    item_dimensions:     'item_dimensions_specification',
    item_whats_included: 'item_whats_included',
};

/* ─── Read-only Spec Card ──────────────────────────────────────────────── */
const SpecCard = ({ label, icon, accent, bg, border, value }) => {
    const [expanded, setExpanded] = useState(false);
    const isEmpty = !value || value === 'NA';

    return (
        <div className="foid-spec-card" style={{ background: bg, border: `1px solid ${border}`, borderRadius: 14, overflow: 'hidden' }}>
            <div
                style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '14px 16px', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setExpanded(e => !e)}
            >
                <span style={{ fontSize: 16 }}>{icon}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.8px', flex: 1 }}>
                    {label}
                </span>
                {!expanded && !isEmpty && (
                    <span style={{ fontSize: 12, color: '#6B7280', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>
                        {value}
                    </span>
                )}
                {isEmpty && !expanded && (
                    <span style={{ fontSize: 11, color: '#D1D5DB', fontStyle: 'italic', marginRight: 8 }}>Not set</span>
                )}
                <span style={{ fontSize: 10, color: accent, fontWeight: 700, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s' }}>▼</span>
            </div>
            <div className="foid-expand-content" style={{ maxHeight: expanded ? '500px' : 0, opacity: expanded ? 1 : 0 }}>
                <div style={{ padding: '0 16px 16px', fontSize: 13.5, color: '#1a1a1a', fontWeight: 500, lineHeight: 1.6 }}>
                    {isEmpty
                        ? <span style={{ color: '#9CA3AF', fontStyle: 'italic', fontWeight: 400 }}>Not specified</span>
                        : value
                    }
                </div>
            </div>
        </div>
    );
};

/* ─── Spec Input (used inside BulkEditModal) ───────────────────────────── */
const SpecInput = React.memo(({ spec, value, onChange }) => {
    const { key, label, icon, accent, border, bg } = spec;
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
                <span style={{ fontSize: 15 }}>{icon}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.7px' }}>
                    {label}
                </span>
            </div>
            <textarea
                className="foid-modal-textarea"
                value={value}
                onChange={(e) => onChange(key, e.target.value)}
                rows={key === 'item_desc' ? 4 : 2}
                placeholder={`Enter ${label.toLowerCase()}…`}
                style={{
                    width: '100%', boxSizing: 'border-box', borderRadius: 10,
                    border: `1.5px solid ${border}`, background: bg,
                    padding: '11px 14px', fontSize: 13.5, color: '#1a1a1a',
                    resize: 'vertical', fontFamily: '"DM Sans", sans-serif', lineHeight: 1.6
                }}
            />
        </div>
    );
});
// Uncontrolled — no re-render per keystroke, zero lag
const UncontrolledSpecInput = ({ spec, defaultValue, onChange }) => {
    const { key, label, icon, accent, border, bg } = spec;
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 14 }}>{icon}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: accent,
                    textTransform: 'uppercase', letterSpacing: '0.7px' }}>{label}</span>
            </div>
            <textarea
                className="foid-modal-textarea"
                defaultValue={defaultValue}          // uncontrolled
                onChange={(e) => onChange(key, e.target.value)}
                rows={key === 'item_desc' ? 3 : 2}
                placeholder={`Enter ${label.toLowerCase()}…`}
                style={{
                    width: '100%', boxSizing: 'border-box', borderRadius: 10,
                    border: `1.5px solid ${border}`, background: bg,
                    padding: '10px 12px', fontSize: 13, color: '#1a1a1a',
                    resize: 'vertical', fontFamily: '"DM Sans", sans-serif', lineHeight: 1.6
                }}
            />
        </div>
    );
};

/* ─── Bulk Edit Modal ──────────────────────────────────────────────────── */
const BulkEditModal = ({ initialValues, onSave, onClose, saving }) => {
    // Store draft in a ref — NO re-render on every keystroke
    const draftRef = useRef({ ...initialValues });

    const handleSave = () => onSave({ ...draftRef.current });

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 999,
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
                backdropFilter: 'blur(6px)' }}
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 24, width: '95%', maxWidth: 980,
                    minWidth: 760, maxHeight: '92vh', display: 'flex', flexDirection: 'column',
                    overflow: 'hidden', boxShadow: '0 24px 72px rgba(0,0,0,.28)' }}
            >
                {/* Header */}
                <div style={{ padding: '20px 28px 16px', borderBottom: '1px solid #F1F3F9',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <div>
                        <div style={{ fontFamily: '"Playfair Display", serif', fontSize: 20,
                            fontWeight: 700, color: '#1B2A4A' }}>✏️ Edit</div>
                        <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 3 }}>
                            Update item specifications and save changes
                        </div>
                    </div>
                    <button onClick={onClose} disabled={saving}
                        style={{ background: '#F3F4F6', border: 'none', borderRadius: 10,
                            width: 34, height: 34, fontSize: 16, cursor: saving ? 'not-allowed' : 'pointer',
                            color: '#6B7280', fontWeight: 700 }}>✕</button>
                </div>

                {/* Body */}
                <div style={{ overflowY: 'auto', padding: '24px 28px', flex: 1 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                        {/* Description — full width */}
                        <UncontrolledSpecInput
                            spec={SPECS[0]}
                            defaultValue={draftRef.current[SPECS[0].key] || ''}
                            onChange={(key, val) => { draftRef.current[key] = val; }}
                        />

                        {/* 2-col grid for the rest */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            {SPECS.slice(1).map(spec => (
                                <UncontrolledSpecInput
                                    key={spec.key}
                                    spec={spec}
                                    defaultValue={draftRef.current[spec.key] || ''}
                                    onChange={(key, val) => { draftRef.current[key] = val; }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{ padding: '16px 28px', borderTop: '1px solid #F1F3F9',
                    display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
                    <button onClick={onClose} disabled={saving}
                        style={{ background: '#F3F4F6', color: '#374151', border: 'none',
                            borderRadius: 10, padding: '9px 20px', fontSize: 13, fontWeight: 600,
                            cursor: 'pointer' }}>Cancel</button>
                    <button onClick={handleSave} disabled={saving}
                        style={{ background: saving ? '#6B7280' : '#1B2A4A', color: '#fff',
                            border: 'none', borderRadius: 10, padding: '9px 24px', fontSize: 13,
                            fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 8 }}>
                        {saving ? (
                            <>
                                <span style={{ width: 13, height: 13, borderRadius: '50%',
                                    border: '2px solid rgba(255,255,255,.35)', borderTopColor: '#fff',
                                    animation: 'spin .8s linear infinite' }} />
                                Saving...
                            </>
                        ) : '💾 Save All Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─── Main Component ───────────────────────────────────────────────────── */
const FurnitureOneItemDetails = (props) => {
    useEffect(() => { injectPageCSS(); }, []);

    const history  = useHistory();
    const location = useLocation();

    // location.state is the instant fallback for initial render
    const stateData = location.state || {};
    const item      = stateData.item  || {};
    const leadId    = stateData.leadId || null;

    // itemId for API calls always comes from URL query param
    const urlParams = new URLSearchParams(location.search);
    const itemId    = urlParams.get('itemId') || item.item_id;

    const [liveItem,      setLiveItem]      = useState({});
    const [itemLoading,   setItemLoading]   = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [saving,        setSaving]        = useState(false);
    const [imgError,      setImgError]      = useState(false);
    const [activeTab,     setActiveTab]     = useState('overview');
    const [imgLoaded,     setImgLoaded]     = useState(false);
    const [imagePreviewOpen, setImagePreviewOpen] = useState(false);

    const { userDetails } = useSelector(state => state.user);
    const isAGMDGM = userDetails?.roles?.some(r => r?.name?.includes('DGM Sales'))
        || userDetails?.roles?.some(r => r?.name?.includes('AGM Sales'))
        || userDetails?.roles?.some(r => r?.name?.includes('DGM Design'))
        || userDetails?.roles?.some(r => r?.name?.includes('AGM Design'));

    /* ── Single fetch: Zoho (live price/stock) + MongoDB (specs/image) merged on backend ── */
    useEffect(() => {
        const fetchItem = async () => {
            if (!itemId) return;
            try {
                setItemLoading(true);
                const res  = await HttpService.getSingleZohoItem(itemId);
                const data = res?.data?.item || {};
                setLiveItem({
                    // Live from Zoho
                    name:       data.name            || '',
                    rate:       data.rate            || 0,
                    label_rate: data.label_rate      || 0,
                    stock:      data.available_stock ?? 0,
                    unit:       data.unit            || '',
                    group_name: data.category_name   || '',
                    sku:        data.sku             || '',
                    item_id:    data.item_id         || itemId,
                    // Specs + image from MongoDB (merged on backend)
                    img:                data.image_url_erp                 || '',
                    item_desc:          data.item_description              || '',
                    item_finish:        data.item_finish_details           || '',
                    // item_color:         data.item_color_specification      || '',
                    item_material:      data.item_material_specification   || '',
                    item_dimensions:    data.item_dimensions_specification || '',
                    item_whats_included: data.item_whats_included          || '',
                });
            } catch (err) {
                console.error('Failed to fetch item:', err);
            } finally {
                setItemLoading(false);
            }
        };
        fetchItem();
    }, [itemId]);

    // location.state is instant fallback; liveItem overwrites once loaded
    const merged = { ...item, ...liveItem };

    const labelRate = Number(merged.label_rate || merged.mrp || merged.list_price || 0);
    const basePrice = Number(merged.rate || 0);
    const discountPercentage = labelRate > 0 && basePrice > 0
        ? Math.round(((labelRate - basePrice) / labelRate) * 100)
        : 0;

    const resolvedDesc = merged.item_desc && merged.item_desc !== 'NA'
        ? merged.item_desc
        : (merged.description || '');

    const specValue = (key) => key === 'item_desc' ? resolvedDesc : (merged[key] || '');

    const currentSpecValues = SPECS.reduce((acc, { key }) => {
        acc[key] = specValue(key);
        return acc;
    }, {});

    /* ── Save spec edits ── */
    const handleSaveAll = async (draft) => {
        try {
            setSaving(true);
            const payload = {};
            SPECS.forEach(({ key }) => {
                payload[FIELD_MAP[key] || key] = draft[key] ?? '';
            });
            await HttpService.updateFurnitureItemAll(itemId, payload);

            // Reflect saved values immediately without refetching
            setLiveItem(prev => ({
                ...prev,
                item_desc:           draft.item_desc           || '',
                item_material:       draft.item_material       || '',
                item_finish:         draft.item_finish         || '',
                // item_color:          draft.item_color          || '',
                item_dimensions:     draft.item_dimensions     || '',
                item_whats_included: draft.item_whats_included || '',
            }));

            setShowEditModal(false);
            props.setSnackInfo('Specifications updated successfully!', 'success');
        } catch (err) {
            console.error('Save failed:', err);
            props.setSnackInfo('Failed to update. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    /* ── Stock meta ── */
    const stock = merged.stock ?? 0;
    const stockMeta =
        stock > 10 ? { color: '#065F46', bg: '#D1FAE5', dot: '#10B981', label: `${stock} In Stock`,  pulse: true  } :
        stock > 0  ? { color: '#92400E', bg: '#FEF3C7', dot: '#F59E0B', label: `Only ${stock} left`, pulse: false } :
                     { color: '#991B1B', bg: '#FEE2E2', dot: '#EF4444', label: 'Out of Stock',        pulse: false };


    /* ── Sub-components ─────────────────────────────────────────────────── */
    const Breadcrumb = () => (
        <div style={{ padding: '12px 32px', background: '#fff', borderBottom: '1px solid #F1F3F9', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#9CA3AF' }}>
            <span style={{ cursor: 'pointer', color: '#1B2A4A', fontWeight: 600 }}> Projects</span>
            <span className="foid-divider-dot" />
            <span>Items Catalogue</span>
            <span className="foid-divider-dot" />
            <span style={{ color: '#4B5563', fontWeight: 500, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{merged.name}</span>
        </div>
    );

    const TabBtn = ({ id, label }) => (
        <button
            className={`foid-tab-btn ${activeTab === id ? 'active' : ''}`}
            onClick={() => setActiveTab(id)}
            style={{ background: 'none', border: 'none', outline: 'none', cursor: 'pointer', padding: '13px 20px', fontSize: 14, color: activeTab === id ? '#1B2A4A' : '#6B7280', fontFamily: '"DM Sans", sans-serif', marginBottom: -1, borderRadius: '4px 4px 0 0' }}
        >
            {label}
        </button>
    );

    const OverviewTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {isAGMDGM && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                    <button
                        className="foid-edit-specs-btn"
                        onClick={() => setShowEditModal(true)}
                        style={{ background: '#1B2A4A', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 2px 10px rgba(27,42,74,.18)' }}
                    >
                        <span style={{ fontSize: 15 }}>✏️</span>
                        Edit Specifications
                    </button>
                </div>
            )}
            {itemLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#F8F9FD', borderRadius: 10, border: '1px solid #EAEFF8', fontSize: 13, color: '#6B7280' }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #E5E7EB', borderTopColor: '#1B2A4A', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                    Loading specifications…
                </div>
            )}
            {SPECS.map(({ key, label, icon, accent, bg, border }) => (
                <SpecCard key={key} label={label} icon={icon} accent={accent} bg={bg} border={border} value={specValue(key)} />
            ))}
        </div>
    );

    const PricingTab = () => {
        const Row = ({ label, value, bold, strike }) => (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #F1F3F9' }}>
                <span style={{ fontSize: 14, color: '#6B7280', fontWeight: 500 }}>{label}</span>
                <span style={{ fontSize: bold ? 24 : 18, fontWeight: bold ? 800 : 600, color: strike ? '#ff0000ff' : '#1B2A4A', textDecoration: strike ? 'line-through' : 'none' }}>
                    {fmt(value)}
                </span>
            </div>
        );
        return (
            <div style={{ background: '#F8F9FD', border: '1px solid #EAEFF8', borderRadius: 16, padding: '10px 24px' }}>
                {labelRate > 0 && <Row label="MRP" value={labelRate} strike />}
                <Row label="Base Price" value={basePrice} bold />
                {discountPercentage > 0 && (
                    <div style={{ marginTop: 16, display: 'inline-flex', alignItems: 'center', background: '#ECFDF5', color: '#065F46', padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 700 }}>
                        💰 Save {discountPercentage}% vs MRP
                    </div>
                )}
            </div>
        );
    };

    const DeliveryTab = () => {
        const cards = [
            { icon: '🚚', title: 'In-Stock Delivery',  desc: 'Items with available stock are typically dispatched within 3–5 business days and delivered within 7–14 working days depending on location.',                                                                tag: 'Standard',           tagColor: '#059669', tagBg: '#D1FAE5' },
            { icon: '🏭', title: 'Made-to-Order',       desc: 'Out-of-stock items are custom-manufactured. Please inform the customer of an expected lead time of 60–90 days from the date of payment confirmation.',                                                      tag: 'Extended Lead Time', tagColor: '#92400E', tagBg: '#FEF3C7' },
            { icon: '🛡️', title: 'Warranty',            desc: 'All Furniture One products carry a 1-year structural warranty. Finish defects are covered for 6 months from date of delivery.',                                                                              tag: '1 Year',             tagColor: '#5B21B6', tagBg: '#EDE9FE' },
        ];
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {cards.map(({ icon, title, desc, tag, tagColor, tagBg }) => (
                    <div key={title} style={{ display: 'flex', gap: 16, background: '#F8F9FD', border: '1px solid #EAEFF8', borderRadius: 14, padding: '18px 20px' }}>
                        <div style={{ fontSize: 28, flexShrink: 0, marginTop: 2 }}>{icon}</div>
                        <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                <span style={{ fontSize: 14, fontWeight: 700, color: '#1B2A4A' }}>{title}</span>
                                <span style={{ background: tagBg, color: tagColor, fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 99, letterSpacing: '0.3px' }}>{tag}</span>
                            </div>
                            <p style={{ margin: 0, fontSize: 13.5, color: '#4B5563', lineHeight: 1.7 }}>{desc}</p>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    /* ─── Render ────── */
    return (
        <div className="foid-page" style={{ minHeight: '100vh', background: '#F0F2F8', fontFamily: '"DM Sans", sans-serif',paddingTop: 12 }}>

            {showEditModal && (
                <BulkEditModal
                    initialValues={currentSpecValues}
                    onSave={handleSaveAll}
                    onClose={() => { if (!saving) setShowEditModal(false); }}
                    saving={saving}
                />
            )}

            {/* Nav Header */}
            <div style={{ background: '#1B2A4A', padding: '0 32px', display: 'flex', alignItems: 'center', gap: 14, height: 62, boxShadow: '0 2px 16px rgba(0,0,0,.22)', position: 'sticky', top: 0, zIndex: 100 }}>
                <span style={{ color: '#fff', fontFamily: '"Playfair Display", serif', fontSize: 19, fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {merged.name}
                </span>
                <span style={{ background: '#F59E0B', color: '#1B2A4A', fontSize: 11, fontWeight: 800, padding: '4px 13px', borderRadius: 99, textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>
                    {merged.group_name}
                </span>
                <span style={{ background: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.55)', fontSize: 11, fontFamily: 'monospace', padding: '4px 12px', borderRadius: 6, flexShrink: 0 }}>
                    {merged.sku}
                </span>
            </div>

            <Breadcrumb />

            {/* Main layout */}
            <div style={{ maxWidth: 1160, margin: '0 auto', padding: '32px 24px 60px', boxSizing: 'border-box' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: 24, alignItems: 'start' }}>

                    {/* LEFT — sticky image + price card */}
                    <div style={{ position: 'sticky', top: 86 }}>
                        <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 2px 20px rgba(27,42,74,.09)', overflow: 'hidden' }}>

                            {/* Image */}
                            <div className="foid-img-wrap" style={{ overflow: 'hidden', background: '#F8F9FD', position: 'relative', height: 310 }}>
                                {!imgLoaded && !imgError && (
                                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F0F2F8' }}>
                                        <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid #E5E7EB', borderTopColor: '#1B2A4A', animation: 'spin 0.8s linear infinite' }} />
                                    </div>
                                )}
                                {merged.img && merged.img !== 'NA' && !imgError ? (
                                    <img
                                        src={merged.img}
                                        alt={merged.name}
                                        onClick={() => setImagePreviewOpen(true)}
                                        onLoad={() => setImgLoaded(true)}
                                        onError={() => { setImgError(true); setImgLoaded(true); }}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: imgLoaded ? 1 : 0, transition: 'opacity .4s', cursor: 'zoom-in' }}
                                    />
                                ) : (
                                    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', gap: 12 }}>
                                        <span style={{ fontSize: 52 }}>🪑</span>
                                        <span style={{ fontSize: 13 }}>No image available</span>
                                    </div>
                                )}
                            </div>

                            {/* Price */}
                            <div style={{ padding: '20px 24px', borderBottom: '1px solid #F1F3F9' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                        <div style={{ fontSize: 18, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '1px' }}>MRP</div>
                                        {merged.label_rate > 0 && (
                                            <div style={{ fontSize: 23, fontWeight: 800, color: '#ff0000ff', textDecoration: 'line-through' }}>
                                                {fmt(merged.label_rate)}
                                            </div>
                                        )}
                                    </div>
                                    {discountPercentage > 0 && (
                                        <div style={{ display: 'inline-flex', alignItems: 'center', background: '#ECFDF5', color: '#065F46', padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                            💰 Save {discountPercentage}% vs MRP
                                        </div>
                                    )}
                                </div>
                                <div className="foid-price-shine" style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1 }}>
                                    {fmt(merged.rate)}
                                </div>
                            </div>

                            {/* Stock badge */}
                            <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div className={stockMeta.pulse ? 'foid-badge-pulse' : ''}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: stockMeta.bg, color: stockMeta.color, padding: '8px 16px', borderRadius: 99, fontSize: 13, fontWeight: 700 }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: stockMeta.dot, display: 'inline-block', flexShrink: 0 }} />
                                    {stockMeta.label}
                                </div>
                            </div>

                            {stock > 0 && stock <= 5 && (
                                <div style={{ margin: '0 16px 16px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 10, padding: '10px 14px', fontSize: 12.5, color: '#92400E', lineHeight: 1.5 }}>
                                    ⚠️ Only {stock} unit{stock > 1 ? 's' : ''} remaining. Restocking may take <strong>60–90 days</strong>.
                                </div>
                            )}
                            {stock === 0 && (
                                <div style={{ margin: '0 16px 16px', background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 10, padding: '10px 14px', fontSize: 12.5, color: '#991B1B', lineHeight: 1.5 }}>
                                    🚫 Out of stock. Delivery approx. <strong>60–90 days</strong> from payment date.
                                </div>
                            )}
                        </div>

                        {/* Quick meta */}
                        <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(27,42,74,.07)', padding: '18px 22px', marginTop: 16 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 14 }}>Quick Info</div>
                            {[
                                { label: 'Category', value: merged.group_name },
                                { label: 'Unit',     value: merged.unit || 'pc' },
                            ].map(({ label, value }) => (
                                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F8F9FD' }}>
                                    <span style={{ fontSize: 12.5, color: '#6B7280' }}>{label}</span>
                                    <span style={{ fontSize: 12.5, fontWeight: 600, color: '#1B2A4A' }}>{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT — tabbed detail */}
                    <div>
                        <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 2px 16px rgba(27,42,74,.07)', padding: '28px 32px 0', overflow: 'hidden' }}>
                            <h1 style={{ fontFamily: '"Playfair Display", serif', fontSize: 30, fontWeight: 700, color: '#1B2A4A', margin: '0 0 4px', lineHeight: 1.2 }}>
                                {merged.name}
                            </h1>
                            {(resolvedDesc || merged.description) && (
                                <p style={{ margin: '0 0 20px', fontSize: 13.5, color: '#6B7280', fontWeight: 400 }}>
                                    {resolvedDesc || merged.description}
                                </p>
                            )}
                            <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #F1F3F9', marginTop: 8 }}>
                                <TabBtn id="overview" label="📋 Overview" />
                                <TabBtn id="pricing"  label="💰 Pricing & Tax" />
                                <TabBtn id="delivery" label="🚚 Delivery & Warranty" />
                            </div>
                        </div>
                        <div style={{ background: '#fff', borderRadius: '0 0 20px 20px', boxShadow: '0 2px 16px rgba(27,42,74,.07)', padding: '28px 32px 32px' }}>
                            {activeTab === 'overview' && <OverviewTab />}
                            {activeTab === 'pricing'  && <PricingTab />}
                            {activeTab === 'delivery' && <DeliveryTab />}
                        </div>
                    </div>

                </div>
            </div>

            {/* Image preview lightbox */}
            {imagePreviewOpen && (
                <div
                    onClick={() => setImagePreviewOpen(false)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 30, cursor: 'zoom-out' }}
                >
                    <img
                        src={merged.img}
                        alt={merged.name}
                        onClick={e => e.stopPropagation()}
                        style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,.5)' }}
                    />
                    <button
                        onClick={() => setImagePreviewOpen(false)}
                        style={{ position: 'absolute', top: 20, right: 25, width: 42, height: 42, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,.15)', color: '#fff', fontSize: 22, cursor: 'pointer' }}
                    >
                        ✕
                    </button>
                </div>
            )}
        </div>
    );
};

const mapDispatchToProps = { setSnackInfo };
export default connect(null, mapDispatchToProps)(FurnitureOneItemDetails);